/* ═══════════════════════════════════════════════════════════════════════════════════════════════════════════
   PROFILE WORDS for the top of the results page: one to three short words about WHO she is ("Pregnant",
   "Vegetarian", "Smoker", "Strength training"), read from her quiz answers.

   Rules of the road
   - Each word describes her life, not what she wants to improve (the focus areas already say that, in their
     own bubbles), and never judges her: no "inactive", "heavy drinker", "stressed".
   - Only what a stranger glancing at the screen can see without harm. Medication, diagnoses and risk markers
     (statins, thyroid, cholesterol, blood pressure, fertility of a partner, skin tone, age) are NEVER used, the
     same line the results copy in the worker draws ("a private reason never names itself").
   - Every word is a RULE below: { group, weight, label, when }. The highest weights win, one word per group, at
     most three words. Groups keep the three words different from each other ("Vegan" and "Plant-forward" never
     both appear; "Smoker" and "Occasional smoker" never both).
   - English only for now, like the rest of the results copy.

   API:  PPProfileTraits.derive(answers, { stage: 'pregnant'|'breastfeeding'|'postnatal'|'trying'|'planning'|'none', max: 3 })
         -> { traits: ['Pregnant', 'Vegetarian', 'Smoker'], all: [{ label, group, weight }, ...] }
   `stage` is the life stage the results page already works out (lifeStageFrom); without it the answer
   pregnancy_phase is read directly.
   The same file also holds suggestAreas(), which tops up the focus-area bubbles (see the block further down).
   ═══════════════════════════════════════════════════════════════════════════════════════════════════════════ */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.PPProfileTraits = api;
})(typeof window !== 'undefined' ? window : globalThis, function () {
  'use strict';

  function list(v) { return Array.isArray(v) ? v : (v ? [v] : []); }
  function has(v, x) { return list(v).indexOf(x) !== -1; }
  function hasAny(v, xs) { for (var i = 0; i < xs.length; i++) { if (has(v, xs[i])) return true; } return false; }
  function noSpecialDiet(a) { return !a.special_diet || a.special_diet === 'none'; }

  var RULES = [
    /* ── life stage: the most defining thing about her right now ── */
    { group: 'stage', weight: 100, label: 'Pregnant',             when: function (a, c) { return c.stage === 'pregnant'; } },
    { group: 'stage', weight: 99,  label: 'Breastfeeding',        when: function (a, c) { return c.stage === 'breastfeeding'; } },
    { group: 'stage', weight: 98,  label: 'Postpartum',           when: function (a, c) { return c.stage === 'postnatal'; } },
    { group: 'stage', weight: 97,  label: 'Trying to conceive',   when: function (a, c) { return c.stage === 'trying'; } },
    { group: 'stage', weight: 96,  label: 'Planning a pregnancy', when: function (a, c) { return c.stage === 'planning'; } },
    { group: 'stage', weight: 95,  label: 'Perimenopause',        when: function (a) { return hasAny(a.menopause_stage, ['early_peri', 'late_peri']) || (has(a.hormone_changes_challenges, 'peri_meno') && !a.menopause_stage); } },
    { group: 'stage', weight: 95,  label: 'Post-menopause',       when: function (a) { return a.menopause_stage === 'post_menopause'; } },
    { group: 'stage', weight: 94,  label: 'Menopause transition', when: function (a) { return a.menopause_stage === 'not_sure'; } },

    /* ── how she eats ── */
    { group: 'diet', weight: 82, label: 'Vegan',       when: function (a) { return a.special_diet === 'vegan'; } },
    { group: 'diet', weight: 82, label: 'Vegetarian',  when: function (a) { return a.special_diet === 'vegetarian'; } },
    { group: 'diet', weight: 82, label: 'Pescatarian', when: function (a) { return a.special_diet === 'pescatarian'; } },
    { group: 'diet', weight: 82, label: 'Keto',        when: function (a) { return a.special_diet === 'keto'; } },
    { group: 'diet', weight: 62, label: 'Plant-forward', when: function (a) { return noSpecialDiet(a) && hasAny(a.meat_eggs_per_week, ['never', '1_2']); } },

    /* ── smoking: a fact she gave us, stated plainly ── */
    { group: 'smoking', weight: 76, label: 'Smoker',            when: function (a) { return a.smoking === 'yes_regularly'; } },
    { group: 'smoking', weight: 76, label: 'Occasional smoker', when: function (a) { return a.smoking === 'sometimes'; } },

    /* ── how she moves ── */
    { group: 'activity', weight: 72, label: 'Very active', when: function (a) { return a.activity_level_weeks === 'intense'; } },
    { group: 'training', weight: 66, label: 'Strength training', when: function (a) { return has(a.exercise_type, 'weights'); } },
    { group: 'training', weight: 65, label: 'Cardio lover',      when: function (a) { return has(a.exercise_type, 'cardio'); } },
    { group: 'training', weight: 64, label: 'Team sports',       when: function (a) { return has(a.exercise_type, 'sports'); } },
    { group: 'training', weight: 63, label: 'Yoga & Pilates',    when: function (a) { return has(a.exercise_type, 'yoga'); } },
    { group: 'training', weight: 50, label: 'Daily walker',      when: function (a) { return has(a.exercise_type, 'general_movement') && !hasAny(a.exercise_type, ['weights', 'cardio', 'sports', 'yoga']); } },

    /* ── the shape of her days ── */
    { group: 'work', weight: 68, label: 'Shift work',         when: function (a) { return has(a.sleep_causes, 'irregular_hours'); } },
    { group: 'work', weight: 67, label: 'Caring for others',  when: function (a) { return has(a.stress_causes, 'caring'); } },
    { group: 'work', weight: 66, label: 'Busy schedule',      when: function (a) { return has(a.stress_causes, 'work_study'); } },
    { group: 'screens', weight: 52, label: 'Long screen days', when: function (a) { return a.baseline_screen_time === '8_plus' || a.screen_time_hours === '8_plus'; } },
    { group: 'sun', weight: 50, label: 'Mostly indoors',       when: function (a) { return a.sunshine_20min === 'rarely'; } },
    { group: 'sun', weight: 50, label: 'Outdoorsy',            when: function (a) { return a.sunshine_20min === 'every_day'; } },

    /* ── what she drinks ── */
    { group: 'caffeine', weight: 58, label: 'Caffeine fan',  when: function (a) { return hasAny(a.caffeine_daily, ['3_4_cups', '5_plus_cups']); } },
    { group: 'caffeine', weight: 58, label: 'Caffeine-free', when: function (a) { return a.caffeine_daily === 'none_very_little'; } },
    { group: 'alcohol',  weight: 57, label: 'Alcohol-free',  when: function (a) { return a.alcohol_per_week === 'none'; } },

    /* ── what she leaves out (read as the diet she keeps, never as a diagnosis) ── */
    { group: 'avoid', weight: 56, label: 'Gluten-free',  when: function (a) { return has(a.food_allergies, 'gluten'); } },
    { group: 'avoid', weight: 56, label: 'Lactose-free', when: function (a) { return has(a.food_allergies, 'lactose'); } },
    { group: 'avoid', weight: 56, label: 'Dairy-free',   when: function (a) { return has(a.food_allergies, 'milk'); } },
    { group: 'avoid', weight: 56, label: 'Egg-free',     when: function (a) { return has(a.food_allergies, 'egg'); } },
    { group: 'avoid', weight: 56, label: 'Soy-free',     when: function (a) { return has(a.food_allergies, 'soy'); } },
    { group: 'avoid', weight: 56, label: 'Nut-free',     when: function (a) { return hasAny(a.food_allergies, ['peanut', 'tree_nuts']); } },
    { group: 'avoid', weight: 56, label: 'Seafood-free', when: function (a) { return hasAny(a.food_allergies, ['fish', 'crustaceans', 'molluscs']) && !has(['pescatarian'], a.special_diet); } },

    /* ── her relationship with supplements ── */
    { group: 'experience', weight: 54, label: 'New to supplements',  when: function (a) { return a.supplement_experience === 'never_tried'; } },
    { group: 'experience', weight: 53, label: 'Supplement-savvy',    when: function (a) { return a.supplement_experience === 'yes_worked'; } },
    { group: 'experience', weight: 40, label: 'Daily multivitamin',  when: function (a) { return a.daily_multivitamin === 'yes_most_days'; } },

    /* ── what her plate looks like ── */
    { group: 'eating', weight: 49, label: 'Protein-focused',     when: function (a) { return a.protein_intake === 'high'; } },
    { group: 'eating', weight: 48, label: 'Veg lover',           when: function (a) { return a.fruits_veg_per_day === '6_plus'; } },
    { group: 'eating', weight: 47, label: 'Fish eater',          when: function (a) { return hasAny(a.fish_seafood_per_week, ['3_4', '5_plus']) && noSpecialDiet(a); } },
    { group: 'eating', weight: 46, label: 'Fermented-food fan',  when: function (a) { return a.fermented_foods === 'most_days'; } },
    { group: 'eating', weight: 45, label: 'Well hydrated',       when: function (a) { return a.water_per_day === '3_plus_l'; } }
  ];

  function stageFromAnswers(a) {
    var p = a.pregnancy_phase;
    return { pregnant: 'pregnant', breastfeeding: 'breastfeeding', postnatal: 'postnatal', trying: 'trying', planning: 'planning' }[p] || 'none';
  }

  function derive(answers, opts) {
    var a = answers || {};
    var o = opts || {};
    var ctx = { stage: o.stage || stageFromAnswers(a) };
    var max = o.max == null ? 3 : o.max;

    var matched = [];
    for (var i = 0; i < RULES.length; i++) {
      var r = RULES[i];
      var ok = false;
      try { ok = !!r.when(a, ctx); } catch (e) { ok = false; }
      if (ok) matched.push({ label: r.label, group: r.group, weight: r.weight, order: i });
    }
    matched.sort(function (x, y) { return (y.weight - x.weight) || (x.order - y.order); });

    var used = {};
    var traits = [];
    for (var j = 0; j < matched.length && traits.length < max; j++) {
      if (used[matched[j].group]) continue;
      used[matched[j].group] = true;
      traits.push(matched[j].label);
    }
    return { traits: traits, all: matched };
  }

  /* ═══════════════════════════════════════════════════════════════════════════════════════════════════════
     SUGGESTED FOCUS AREAS: when she picked fewer than three areas, the page tops the bubbles up with areas her
     OTHER answers point to. Only areas she did NOT pick, and only from what she actually answered:
       - the six baseline questions every shopper gets (energy, sleep, digestion, stress, colds, focus), screen time;
       - the follow-up answers inside an area, when they are there;
       - her life stage (pregnancy, breastfeeding...).
     Each rule has a strength: 2 = a clear sign ("low most of the day", "poor sleep"), 1 = a milder one ("a dip in the
     afternoon", "hit or miss"), 3 = a life stage. An area is suggested when its strength is at least 1 (David chose
     "milder signs count too"); the strongest come first, ties keep the order below. Nothing is invented: with no
     matching answer nothing is added, so the row can stay shorter than three.
     Not used on purpose: Men's Health (its signs are private, see above), Hair/Skin/Nails and Longevity (no baseline
     answer points there) and anything about age.
     API:  suggestAreas(answers, { picked: ['sleep', ...], max: 2, stage: 'pregnant' }) -> ['energy', 'mood_stress']
     ═══════════════════════════════════════════════════════════════════════════════════════════════════════ */
  function nonEmpty(v) { return list(v).filter(function (x) { return x && x !== 'none'; }).length > 0; }

  var AREA_ORDER = ['energy', 'sleep', 'mood_stress', 'digestion', 'immunity', 'brain', 'vision', 'womens_health', 'hormonal_balance', 'bones_joints', 'heart', 'sport', 'maternity'];

  var AREA_SIGNALS = [
    { area: 'energy', strength: 2, when: function (a) { return a.baseline_energy === 'low_often' || hasAny(a.energy_day, ['low_most_day', 'low_morning']); } },
    { area: 'energy', strength: 1, when: function (a) { return a.baseline_energy === 'dips_sometimes' || a.energy_day === 'afternoon_dip'; } },

    { area: 'sleep', strength: 2, when: function (a) { return a.baseline_sleep === 'poor' || a.sleep_quality === 'struggle_fall_stay'; } },
    { area: 'sleep', strength: 1, when: function (a) { return a.baseline_sleep === 'mixed' || hasAny(a.sleep_quality, ['wake_often', 'hard_to_fall_asleep']) || a.wake_refreshed === 'tired_most_mornings'; } },

    { area: 'mood_stress', strength: 2, when: function (a) { return a.baseline_stress === 'high' || a.stress_levels_lately === 'often_affects_daily' || a.mood_dips === 'often_challenge'; } },
    { area: 'mood_stress', strength: 1, when: function (a) { return a.baseline_stress === 'sometimes' || a.stress_levels_lately === 'sometimes_pressure' || a.mood_dips === 'sometimes'; } },

    { area: 'digestion', strength: 2, when: function (a) { return a.baseline_digestion === 'frequent' || a.digestion_most_days === 'frequent_issues'; } },
    { area: 'digestion', strength: 1, when: function (a) { return a.baseline_digestion === 'occasional' || a.digestion_most_days === 'occasional_issues' || a.bloated_after_meals === 'often'; } },

    { area: 'immunity', strength: 2, when: function (a) { return a.baseline_immunity === 'often' || a.colds_frequency === 'very_often'; } },
    { area: 'immunity', strength: 1, when: function (a) { return a.baseline_immunity === 'some_winters' || a.colds_frequency === 'few_times_year' || a.illness_recovery_time === '7_plus_days'; } },

    { area: 'brain', strength: 2, when: function (a) { return a.baseline_focus === 'struggles' || a.focus_through_day === 'struggle_most_time'; } },
    { area: 'brain', strength: 1, when: function (a) { return a.baseline_focus === 'fades' || hasAny(a.focus_through_day, ['fine_then_fades', 'comes_and_goes']); } },

    { area: 'vision', strength: 2, when: function (a) { return hasAny(a.eye_comfort, ['dry_uncomfortable', 'vision_changing']); } },
    { area: 'vision', strength: 1, when: function (a) { return a.eye_comfort === 'strained_screens' || a.screen_time_hours === '8_plus' || a.baseline_screen_time === '8_plus'; } },

    { area: 'womens_health', strength: 2, when: function (a) { return a.bio_sex !== 'male' && (a.pms_cycle_discomfort === 'stronger' || a.cycles_regular === 'often_unpredictable'); } },
    { area: 'womens_health', strength: 1, when: function (a) { return a.bio_sex !== 'male' && (a.pms_cycle_discomfort === 'mild' || a.cycles_regular === 'sometimes_irregular' || hasAny(a.period_flow, ['heavy', 'very_heavy'])); } },

    { area: 'hormonal_balance', strength: 2, when: function (a) { return a.bio_sex !== 'male' && (hasAny(a.menopause_stage, ['early_peri', 'late_peri', 'post_menopause']) || hasAny(a.hormone_changes_challenges, ['peri_meno', 'hormonal_acne_irregular'])); } },

    { area: 'bones_joints', strength: 2, when: function (a) { return hasAny(a.joints_feel, ['less_flexible', 'soreness_slows']) || hasAny(a.bone_concerns, ['family_history', 'past_fracture', 'past_menopause']); } },
    { area: 'bones_joints', strength: 1, when: function (a) { return a.joints_feel === 'stiff_eases' || a.calcium_from_food === 'not_much'; } },

    { area: 'heart', strength: 2, when: function (a) { return nonEmpty(a.heart_daily_signs); } },
    { area: 'heart', strength: 1, when: function (a) { return a.heart_fitness === 'avoid_effort'; } },

    { area: 'sport', strength: 2, when: function (a) { return a.recovery_soreness === 'often'; } },
    { area: 'sport', strength: 1, when: function (a) { return a.recovery_soreness === 'sometimes' || nonEmpty(a.recovery_slowdowns); } },

    { area: 'maternity', strength: 3, when: function (a, c) { return a.bio_sex !== 'male' && hasAny(c.stage, ['pregnant', 'breastfeeding', 'postnatal']); } },
    { area: 'maternity', strength: 2, when: function (a, c) { return a.bio_sex !== 'male' && c.stage === 'trying'; } }
  ];

  function suggestAreas(answers, opts) {
    var a = answers || {};
    var o = opts || {};
    var ctx = { stage: o.stage || stageFromAnswers(a) };
    var max = o.max == null ? 2 : o.max;
    var taken = {};
    list(o.picked).forEach(function (id) { taken[id] = true; });

    var score = {};
    for (var i = 0; i < AREA_SIGNALS.length; i++) {
      var s = AREA_SIGNALS[i];
      if (taken[s.area]) continue;
      var ok = false;
      try { ok = !!s.when(a, ctx); } catch (e) { ok = false; }
      if (!ok) continue;
      // the strongest sign counts in full, every further sign adds a little, so two clear hints outrank one
      score[s.area] = score[s.area] == null ? s.strength : Math.max(score[s.area], s.strength) + 0.1;
    }
    return AREA_ORDER
      .filter(function (area) { return score[area] != null && score[area] >= 1; })
      .sort(function (x, y) { return (score[y] - score[x]) || (AREA_ORDER.indexOf(x) - AREA_ORDER.indexOf(y)); })
      .slice(0, Math.max(0, max));
  }

  return { derive: derive, suggestAreas: suggestAreas, RULES: RULES };
});
